/**
 * Narrow API client for rehearsal-related admin reads + uploads.
 *
 * Rehearsal CRUD itself is part of `quizContentAdminService.ts` because
 * rehearsals are inline lesson items (created/edited per lesson). This
 * file only holds:
 *  - scenario image presign + upload
 *  - admin attempts read (for the per-rehearsal analytics panel)
 */

import { fetchApi } from '../utils/api';
import { authHeader } from './authToken';

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

/**
 * Roles a tenant has *active* prompt template versions for. Only these are
 * usable when creating a rehearsal, because the analyzer needs to pin a
 * prompt version at save time.
 *
 * feat-251 — REPOINTED. This used to read `GET /api/tenant/{id}/prompt-templates`
 * and derive the list client-side. That route lives in the backend's
 * `prompt_management.py`, which is **corporate-only and stays that way by
 * explicit decision** (the Permissions page lists Prompt Management as `—` for
 * Manager, and that is correct). So once store managers were granted the Learn
 * Module, the rehearsal role dropdown would still have come up empty for them —
 * the Learn Module working "except rehearsals are mysteriously broken".
 *
 * It now reads the purpose-built `GET /api/tenant/{id}/rehearsal-roles`, which
 * is gated for `admin | corporate | manager` and returns role NAMES ONLY —
 * no prompt text, no version bodies, no template ids. Sorting, de-duplication
 * and filtering are the backend's job on this route.
 *
 * ⚠️ INVARIANT NOW HELD BY THE BACKEND — feat-248 (AT6). The set this returns
 * must be restricted to TENANT-DEFAULT templates (`location_id IS NULL`), not
 * merely to templates with a non-null `active_version_id`. `create_rehearsal`
 * (`routes/admin_quiz_content.py:2874`) calls
 * `resolve_prompt_template_version(tenant_id, None, body.role)` — `location_id`
 * is ALWAYS `None` — so a template scoped to a location can never resolve at
 * save time no matter how active its version is. Before feat-248 such a role
 * was offered in the dropdown and then 400'd on save with "No active prompt
 * template version". That filter used to live right here, in the loop this
 * commit deleted; it now has to live in the endpoint's query. There is no
 * frontend test that can catch its loss any more, which is exactly why this
 * note is here.
 *
 * Errors PROPAGATE (feat-251 AC21). This deliberately does not swallow a
 * failure into `[]`: "this tenant has no roles with an active prompt version"
 * is a real, valid state and "the request failed" is a bug, and the caller has
 * to be able to tell them apart. A response that is missing its `roles` array
 * is a failed load, not an empty one, and throws for the same reason.
 */
export async function listRolesWithActivePrompt(
  tenantId: string,
): Promise<string[]> {
  const res = await fetchApi<{ roles?: unknown }>(
    `/api/tenant/${tenantId}/rehearsal-roles`,
    { headers: getAuthHeaders() },
  );
  if (!Array.isArray(res?.roles)) {
    throw new Error(
      'Malformed response from /rehearsal-roles: expected a "roles" array',
    );
  }
  return res.roles as string[];
}

export interface RehearsalAttemptSummary {
  id: string;
  management_id: string;
  attempt_number: number;
  started: string | null;
  ended: string | null;
  passed: boolean | null;
  xp_awarded: number;
  transcript_text: string;
  ai_coaching_text: string;
  analyzer_error: string | null;
}

export async function listRehearsalAttempts(
  rehearsalId: string,
  managementId?: string,
): Promise<{ attempts: RehearsalAttemptSummary[] }> {
  const qs = managementId
    ? `?management_id=${encodeURIComponent(managementId)}`
    : '';
  return fetchApi(`/api/admin/rehearsals/${rehearsalId}/attempts${qs}`, {
    headers: getAuthHeaders(),
  });
}

/**
 * Step 1 of the scenario-image upload: ask the backend for a presigned PUT URL.
 * Step 2 is the direct fetch() to the upload_url with the image bytes.
 */
export async function requestScenarioImageUploadUrl(body: {
  tenant_id: string;
  filename: string;
  content_type: string;
}): Promise<{
  s3_key: string;
  upload_url: string;
  expires_in: number;
  content_type: string;
}> {
  return fetchApi('/api/admin/rehearsals/scenario-image-upload-url', {
    method: 'POST',
    headers: getAuthHeaders(),
    body,
  });
}

export async function uploadScenarioImage(
  file: File,
  tenantId: string,
): Promise<{ s3_key: string }> {
  const presigned = await requestScenarioImageUploadUrl({
    tenant_id: tenantId,
    filename: file.name,
    content_type: file.type || 'image/jpeg',
  });
  const res = await fetch(presigned.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': presigned.content_type },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Image upload failed: ${res.status} ${res.statusText}`);
  }
  return { s3_key: presigned.s3_key };
}
